import {
  each,
  extend,
  find,
} from 'lodash';

/**
 * Model for managing trackers
 */
function Tracker(
  $q,
  Api,
) {
  'ngInject';

  return {
    getTrackers: getTrackers,
    getTrackersWithAccessDetails: getTrackersWithAccessDetails,
  };

  /**
   * @ngdoc method
   * @name getTrackersWithAccessDetails
   * @methodOf adjust.service:Tracker
   *
   * @description
   * Get trackers list extended with details about access
   *
   * @param {string} appToken app token
   * @param {Object} access access details
   * @returns {promise} A promise representing the state of the trackers request
   */
  function getTrackersWithAccessDetails(appToken, access) {
    return getTrackers({
      appToken: appToken,
      includeArchived: true,
      includeCTV: true,
    }).then(function(result) {
      var trackers = result;

      each(access.trackers, function(accessDetails, token) {
        extend(find(trackers, { token: token }), {
          access: accessDetails,
        });
      });

      return trackers;
    });
  }

  /**
   * @ngdoc method
   * @name getTrackers
   * @methodOf adjust.service:Tracker
   *
   * @description
   * Get trackers
   *
   * @param {Object} opt various options for trackers list request
   * @param {String} opt.appToken app's token
   * @param {String} opt.trackerToken trackerToken's token
   * @param {String} opt.hasAttributionSettings check if user has attribution settings enabled
   * @param {String} opt.excludePartnerIds
   * @param {Boolean} opt.includeArchived include archived trackers
   * @returns {promise} A promise representing the state of the trackers request with filtered output
   */
  function getTrackers(opt = {}) {
    return _requestTrackers({
      appToken: null,
      trackerToken: null,
      includeArchived: null,
      excludePartnerIds: null,
      includeCTV: null,
      ...opt,
    }).then(result => result.trackers);
  }

  /**
   * @ngdoc method
   * @name _requestTrackers
   * @methodOf adjust.service:Tracker
   *
   * @description
   * Get tracker request promise based on parameters
   *
   * @param {Object} opt various options for trackers request
   * @param {String} opt.appToken app's token
   * @param {String} opt.trackerToken trackerToken's token
   * @param {Boolean} opt.includeArchived include archived trackers
   * @param {Number} opt.per number of trackers per page
   * @param {Number} opt.page trackers page
   * @param {String=} opt.search search term
   * @param {Boolean} opt.hasAttributionSettings check if user has attribution settings enabled
   * @param {String} opt.excludePartnerIds
   * @returns {Promise} A promise representing the state of the trackers request
   * @private
   */
  function _requestTrackers(opt) {
    var baseParams = {
      include_archived: opt.includeArchived,
      exclude_partner_ids: opt.excludePartnerIds,
      include_ctv: opt.includeCTV,
      search: opt.search || null,
    };

    if (angular.isDefined(opt.hasAttributionSettings)) {
      baseParams.with_special_trackers = opt.hasAttributionSettings;
    }

    if (opt.per || opt.page) {
      baseParams.per = opt.per;
      baseParams.page = opt.page;
    }

    if (opt.trackerToken) {
      return Api.tracker.get(
        extend(
          {
            token: opt.trackerToken,
            action: 'descendants',
          },
          baseParams,
        ),
      ).$promise;
    }

    return Api.dashboard.get(
      extend(
        {
          token: opt.appToken,
          action: 'trackers',
        },
        baseParams,
      ),
    ).$promise;
  }
}

export { Tracker };
